import yellowWalker from "../../../assets/yellowWalker.png";
import AudioSource from "./AudioSource";
import { LngLat, Map } from "mapbox-gl";

import proj4 from "proj4";
import { RouteState } from "../../../api/edits/editRoute.responses";
import {
  MapBoxGLPoint,
  MapBoxGLPointData,
} from "../../../ts/interfaces/mapbox.interfaces";
// definition utm33 projection
let utm33: string =
  'PROJCS["WGS 84 / UTM zone 33N",GEOGCS["WGS 84",DATUM["WGS_1984",SPHEROID["WGS 84",6378137,298.257223563,AUTHORITY["EPSG","7030"]],AUTHORITY["EPSG","6326"]],PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],UNIT["degree",0.0174532925199433,AUTHORITY["EPSG","9122"]],AUTHORITY["EPSG","4326"]],PROJECTION["Transverse_Mercator"],PARAMETER["latitude_of_origin",0],PARAMETER["central_meridian",15],PARAMETER["scale_factor",0.9996],PARAMETER["false_easting",500000],PARAMETER["false_northing",0],UNIT["metre",1,AUTHORITY["EPSG","9001"]],AXIS["Easting",EAST],AXIS["Northing",NORTH],AUTHORITY["EPSG","32633"]]';

// class Walker: position and speed vector of a walker
// - position is in utm33
// - speed magnitude is in m/s
// - speed direction is in degrees, zero is east, positive degrees are clockwise
// - auralization sources are focused and background sounds
// - POI sources are POI audio desctioption sounds

///////////// zamini unload sounds sa remove sounds
//////////////// loop!!!!
//
// napravi klase za poziciju i brzinu

// constructor for class Walker
// eslint-disable-next-line
export default class Walker {
  private posX: number;
  private posY: number;
  private speedMag: number;
  private speedDir: number;
  private AuralizationSources: AudioSource[];
  private POISources: AudioSource[];
  private triggeredPoiIndices: Set<number>;
  public currentPoiIndex: number;
  public isPoiPaused: boolean;

  constructor() {
    this.posX = 0.0;
    this.posY = 0.0;
    this.SetLngLat(16, 43);
    this.speedMag = 0.0;
    this.speedDir = 0.0;
    this.AuralizationSources = [];
    this.POISources = [];
    this.triggeredPoiIndices = new Set();
    this.currentPoiIndex = -1;
    this.isPoiPaused = false;
  }

  // creates audio soruces from pois, fss and bss
  // pre: route has all focused, bacground and POI sounds
  async InitializeAudioSources(
    route: RouteState,
    currentLanguage: string
  ): Promise<void> {
    const baseUrl = import.meta.env.VITE_DATABASE_URL;

    async function resolveUrl(pointId: number, params: string): Promise<string> {
      const res = await fetch(`${baseUrl}/edits/routes/stream/${pointId}?${params}`);
      const { url } = await res.json();
      return url;
    }

    if (this.POISources.length === 0) {
      const resolved = await Promise.all(
        route.pointsOfInterest.map(async (poi) => {
          const coord = proj4(utm33).forward([poi.longitude, poi.latitude]);
          const url = await resolveUrl(poi.id, `routeEditId=${route.id}&pointType=poi&languageCode=${currentLanguage}`);
          return { coord, url, poi };
        })
      );
      for (const { coord, url, poi } of resolved) {
        this.POISources.push(new AudioSource(coord[0], coord[1], "none", poi.radius, url, false));
      }
    }

    if (this.AuralizationSources.length === 0) {
      const [bsResolved, fsResolved] = await Promise.all([
        Promise.all(
          route.backgroundSounds.map(async (bs) => {
            const coord = proj4(utm33).forward([bs.longitude, bs.latitude]);
            const url = await resolveUrl(bs.id, `routeEditId=${route.id}&pointType=ambient`);
            return { coord, url, bs };
          })
        ),
        Promise.all(
          route.focusedSounds.map(async (fs) => {
            const coord = proj4(utm33).forward([fs.longitude, fs.latitude]);
            const url = await resolveUrl(fs.id, `routeEditId=${route.id}&pointType=ambient`);
            return { coord, url, fs };
          })
        ),
      ]);
      for (const { coord, url, bs } of bsResolved) {
        this.AuralizationSources.push(new AudioSource(coord[0], coord[1], "lin", bs.radius, url));
      }
      for (const { coord, url, fs } of fsResolved) {
        this.AuralizationSources.push(new AudioSource(coord[0], coord[1], fs.falloffName === "Linear" ? "lin" : "log", fs.radius, url));
      }
    }
  }

  // removes all audio sources
  RemoveAudioSources(): void {
    while (this.AuralizationSources.length > 0) {
      this.AuralizationSources[
        this.AuralizationSources.length - 1
      ].sound.unload();
      this.AuralizationSources.pop();
    }
    while (this.POISources.length > 0) {
      this.POISources[this.POISources.length - 1].sound.unload();
      this.POISources.pop();
    }
    this.triggeredPoiIndices.clear();
    this.currentPoiIndex = -1;
    this.isPoiPaused = false;
  }

  // rotates speedVector clockwise 5 degrees
  RotateCWise(): void {
    this.speedDir = this.speedDir - 5.0;
    if (this.speedDir < 0.0) this.speedDir = this.speedDir + 360.0;
  }

  // rotates speedVector counterclockwise 5 degrees
  RotateCCWise(): void {
    this.speedDir = this.speedDir + 5.0;
    if (this.speedDir > 360.0) this.speedDir = this.speedDir - 360.0;
  }

  // start walking with speed of 5 m/2
  Walk(): void {
    this.speedMag = this.speedMag + 1.4;
  }

  //Decelerate the walking speed
  SlowDown(): void {
    if (this.speedMag > 0.0) this.speedMag = this.speedMag - 1.4;
    else this.speedMag = 0.0;
  }

  // stop walking
  Stop(): void {
    this.speedMag = 0.0;
  }

  // calculate new position after time period
  // pre: deltaTime is time in miliseconds that passed from previous position
  // post: posX and posY have new position of walker
  CalcPosition(deltaTime: number): void {
    this.posX =
      this.posX +
      (deltaTime / 1000.0) *
        this.speedMag *
        Math.cos((this.speedDir * Math.PI) / 180.0);
    this.posY =
      this.posY +
      (deltaTime / 1000.0) *
        this.speedMag *
        Math.sin((this.speedDir * Math.PI) / 180.0);
  }

  // return position in WGS
  GetLngLat(): LngLat {
    const lnglat: LngLat = new LngLat(0.0, 0.0);
    [lnglat.lng, lnglat.lat] = proj4(utm33).inverse([this.posX, this.posY]);
    return lnglat;
  }

  // set position in WGS
  SetLngLat(lng: number, lat: number): void {
    let utm33Coord: number[] = proj4(utm33).forward([lng, lat]);
    this.posX = utm33Coord[0];
    this.posY = utm33Coord[1];
  }

  // unloads all AudioSources
  UnloadSounds(): void {
    for (const as of this.AuralizationSources) {
      as.sound.unload();
    }
    for (const pois of this.POISources) {
      pois.sound.unload();
    }
  }

  // starts playing all auralization sources (muted)
  Start(): void {
    for (const as of this.AuralizationSources) {
      as.sound.volume(0.0);
      as.sound.play();
    }
  }

  // calculates volume and pan for all auralization sources
  //   and triggers it for POI sources
  CalcVolume(): void {
    console.clear();
    // for focused and background sources
    for (const as of this.AuralizationSources) {
      // calculate length
      let dist: number = Math.sqrt(
        Math.pow(as.posX - this.posX, 2.0) + Math.pow(as.posY - this.posY, 2.0)
      );
      // calculate angle from walker to source
      let phi: number = calculateAngle(as.posX, as.posY, this.posX, this.posY);
      // is closer than listening radius
      if (dist > as.radius) {
        as.sound.volume(0.0);
      } else {
        if (as.decay === "lin") {
          // calculates volume linearily
          as.sound.volume((as.radius - dist) / as.radius);
        } else if (as.decay === "log") {
          // calculates volume by pow3
          // maps to interval [0,1]
          let mapInt: number = (as.radius - dist) / as.radius;
          let exp2: number = Math.pow(mapInt, 3.0);
          as.sound.volume(exp2);
        }
        // angle between:
        //    - walker to source angle
        //    - walker direction
        let deltaPhi: number = this.speedDir - phi;
        let pan: number = Math.sin((deltaPhi / 180) * Math.PI);
        // set pan
        as.sound.stereo(pan);
      }
    }

    // for POI sources: trigger on entry, prevent re-trigger until walker exits radius
    for (let i = 0; i < this.POISources.length; i++) {
      const pois = this.POISources[i];
      let dist: number = Math.sqrt(
        Math.pow(pois.posX - this.posX, 2.0) +
          Math.pow(pois.posY - this.posY, 2.0)
      );
      if (dist < pois.radius) {
        if (!this.triggeredPoiIndices.has(i) && !pois.sound.playing() && !this.isPoiPaused) {
          // stop any currently playing POI — stop() also resets position to beginning
          if (this.currentPoiIndex >= 0 && this.currentPoiIndex !== i) {
            this.POISources[this.currentPoiIndex].sound.stop();
          }
          pois.sound.volume(1.0);
          pois.sound.play();
          this.triggeredPoiIndices.add(i);
          this.currentPoiIndex = i;
          this.isPoiPaused = false;
        }
      } else {
        // walker left the radius — allow replay on next entry
        this.triggeredPoiIndices.delete(i);
      }
    }
  }

  // returns current POI playback info, or null if no POI is active
  GetCurrentPOIPlayback(): { current: number; duration: number; isPlaying: boolean } | null {
    if (this.currentPoiIndex < 0 || this.currentPoiIndex >= this.POISources.length) return null;
    const sound = this.POISources[this.currentPoiIndex].sound;
    const current = sound.seek() as number;
    const duration = sound.duration();
    const isPlaying = sound.playing();
    return { current, duration, isPlaying };
  }

  // toggles play/pause on the currently active POI
  TogglePOIPause(): void {
    if (this.currentPoiIndex < 0 || this.currentPoiIndex >= this.POISources.length) return;
    const sound = this.POISources[this.currentPoiIndex].sound;
    if (sound.playing()) {
      sound.pause();
      this.isPoiPaused = true;
    } else {
      sound.play();
      this.isPoiPaused = false;
    }
  }

  // seeks the current POI forward by 10 seconds
  SkipForwardPOI(): void {
    if (this.currentPoiIndex < 0 || this.currentPoiIndex >= this.POISources.length) return;
    const sound = this.POISources[this.currentPoiIndex].sound;
    const current = sound.seek() as number;
    const duration = sound.duration();
    sound.seek(Math.min(current + 10, duration - 0.01));
  }

  // hides walker layer by erasing its source data
  HideWalker(map: Map): void {
    if (map != null) {
      let lay: any = map.getSource("points_walker");
      if (lay !== undefined) {
        let data: MapBoxGLPointData = {
          type: "FeatureCollection",
          features: [],
        };
        lay.setData(data);
      }
      // wlk.MuteVolume();
      this.UnloadSounds();
    }
  }

  // updates walker layer in map
  // pre:   - map has mapbox object on which to display
  //        - wlk has the position and orientation of the walker
  // post:  - map layer walker has the displayed walker
  UpdateWalker(map: Map): void {
    if (map != null && map.isStyleLoaded()) {
      let lay: any = map.getSource("points_walker");
      if (lay !== undefined) {
        lay.setData(FillDataForUpdateWalker(this.GetLngLat()));
      }
      map.setLayoutProperty("walker", "icon-rotate", 360.0 - this.speedDir);
    }
  }
}

// creates walker layer and adds it to map
export function DisplayWalker(map: Map): void {
  map.on("load", () => {
    // Add an image to use as a custom marker
    map.loadImage(yellowWalker, (error, image) => {
      if (error) throw error;
      map.addImage("circleNose", image as ImageBitmap);
      // Add a GeoJSON point source
      map.addSource("points_walker", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });
      // Add a symbol layer
      map.addLayer({
        id: "walker",
        type: "symbol",
        source: "points_walker",
        layout: {
          "icon-image": "circleNose",
          "icon-rotate": 45,
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "text-allow-overlap": true,
          "text-optional": true,
          "text-field": ["get", "title"],
          "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
          "text-offset": [0, 0],
          "text-anchor": "center",
        },
      });
    });
  });
}

// returns data structure for walker layer
// pre:   pos has the position of the walker
// post:  returns array data with coordinates of walker
export function FillDataForUpdateWalker(lnglat: LngLat): MapBoxGLPointData {
  let data: MapBoxGLPointData = {
    type: "FeatureCollection",
    features: [],
  };
  let point: MapBoxGLPoint = {
    type: "Feature",
    geometry: {
      type: "Point",
      // add geometry from route object
      coordinates: [lnglat.lng, lnglat.lat],
    },
    properties: {
      // add name
      title: "",
    },
  };
  data.features.push(point);
  return data;
}

// calculates angle of vector defined by points 1 and 2
// pre: 2D coordinates of two points
// post: angle in degrees, zero is east
function calculateAngle(
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number {
  // Calculate the differences in x and y coordinates
  let deltaX: number = x2 - x1;
  let deltaY: number = y2 - y1;

  // Use atan2 to get the angle in radians
  let angleRadians: number = Math.atan2(deltaY, deltaX);

  // Convert radians to degrees
  let angleDegrees: number = angleRadians * (180 / Math.PI);

  angleDegrees = angleDegrees - 180;
  // Adjust the angle to be within [0, 360) degrees
  if (angleDegrees < 0) {
    angleDegrees += 360;
  }

  return angleDegrees;
}
